import type { ReactNode } from 'react';
import { Clock, Landmark, Send } from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { getTravelModeForTransportCode } from '@/components/map/googleMaps';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatTime } from '@/utils/date';
import { formatCount } from '@/utils/number';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import {
  DCR_APPROVAL_STATUS_LABELS,
  DCR_APPROVAL_STATUS_TONE,
} from '../constants/moTravel.constants';
import { isEndWaypoint } from '../utils/dayRoutes';
import { formatDuration } from '../utils/formatDuration';
import type { StopDetail } from '../utils/routeFocus';
import { formatLegDistance } from '../utils/stopInsights';
import {
  MoEntryCard,
  MoEntryDetailLine,
  MoEntryMapsLink,
  MoEntryStat,
  MoEntryStatGrid,
  type MapsDirections,
} from './MoEntryCard';
import { MoEndIcon } from './MoEndIcon';
import { MoTransportBadge } from './MoTransportBadge';

interface WarningChipProps {
  children: ReactNode;
}

function WarningChip({ children }: WarningChipProps) {
  return (
    <span className={cn('rounded-full px-1.5 text-3xs font-medium', STATUS_COLOR_CLASS.warning)}>
      {children}
    </span>
  );
}

interface MoStopCardProps {
  stopDetail: StopDetail;
  moreStopCount?: number | undefined;
  onClose?: (() => void) | undefined;
  hasTintedBody?: boolean | undefined;
  className?: string | undefined;
}

export function MoStopCard({
  stopDetail,
  moreStopCount = 0,
  onClose,
  hasTintedBody,
  className,
}: MoStopCardProps) {
  const { waypoint, fromTitle, fromPosition, insight, color, date } = stopDetail;
  const { stop } = waypoint;
  if (!stop) return null;

  const title = stop.instituteName ?? stop.name ?? `Stop ${waypoint.label}`;
  const mapsDirections: MapsDirections | null = fromPosition
    ? { from: fromPosition, travelMode: getTravelModeForTransportCode(stop.transportType?.code) }
    : null;

  return (
    <MoEntryCard
      headerColor={color}
      headerMarker={isEndWaypoint(waypoint) ? <MoEndIcon className="size-3" /> : waypoint.label}
      heading={`Stop ${waypoint.label}`}
      date={date}
      closeLabel="Close stop details"
      onClose={onClose}
      hasTintedBody={hasTintedBody}
      className={className}
    >
      <div className="flex items-start gap-2">
        {stop.instituteName && (
          <span
            className={cn(
              'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md',
              ACCENT_MARK_CLASS,
            )}
          >
            <Landmark className="size-3.5" aria-hidden />
          </span>
        )}
        <div className="flex min-w-0 flex-col">
          <span className="text-xs-plus leading-snug font-semibold max-lg:text-sm">{title}</span>
          {fromTitle && (
            <span className="truncate text-2xs text-muted-foreground max-lg:text-xs">
              from <span className="font-semibold">{fromTitle}</span>
            </span>
          )}
          {moreStopCount > 0 && (
            <span className="text-2xs font-medium text-primary">
              +{formatCount(moreStopCount, 'more stop')} here
            </span>
          )}
        </div>
      </div>

      <MoEntryStatGrid>
        <MoEntryStat
          label="Distance"
          value={insight?.distanceKm == null ? null : formatLegDistance(insight.distanceKm)}
          hint="Distance from the previous point"
        />
        <MoEntryStat
          label="Gap"
          value={insight?.gapMinutes == null ? null : formatDuration(insight.gapMinutes)}
          hint="Time between the previous stop and this one"
          isWarning={insight?.isLongGap ?? false}
        />
        <MoEntryStat
          label="Spent"
          value={stop.spendMinutes == null ? null : formatDuration(stop.spendMinutes)}
          hint="Time spent at this stop"
        />
      </MoEntryStatGrid>

      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <MoEntryDetailLine icon={Clock}>
            <span className="tabular-nums">
              {formatTime(stop.timeFrom)} – {formatTime(stop.timeTo)}
            </span>
          </MoEntryDetailLine>
          <MoEntryDetailLine icon={Send}>
            <span className="tabular-nums">Submitted {formatTime(stop.submittedAt)}</span>
            {insight?.lateMinutes != null && (
              <WarningChip>{formatDuration(insight.lateMinutes)} later</WarningChip>
            )}
          </MoEntryDetailLine>
        </div>
        {stop.transportType && (
          <MoTransportBadge transportType={stop.transportType} tintColor={color} size="large" />
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t pt-2">
        {stop.dcrTypeCode && <Badge variant="outline">{stop.dcrTypeCode}</Badge>}
        <StatusBadge tone={DCR_APPROVAL_STATUS_TONE[stop.approvalStatus]}>
          {DCR_APPROVAL_STATUS_LABELS[stop.approvalStatus]}
        </StatusBadge>
        <MoEntryMapsLink position={waypoint.position} directions={mapsDirections} />
      </div>
    </MoEntryCard>
  );
}
